import { z } from 'zod';
import type {
  TypeOfRequestForm,
  NatureOfCrimeForm,
  NDOForm,
  AgentInformationForm,
  TypeOfServiceForm,
  FileUploadForm,
  DomesticReviewForm,
  IdentifierFormValue,
} from '../store/types';
import { usPhoneSchema, internationalPhoneSchema } from '@/utils/phoneUtils';
import { acknowledged, answered, firstErrorPerField, requiredEmail, requiredText } from '@/utils/formSchemas';
import { isIdentifierTypeSupported, isDataCategorySupported } from '../store/cmsMapping';

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
  firstErrorField?: string;
}

export function scrollToFirstError(errors: Record<string, string>): void {
  const firstErrorKey = Object.keys(errors)[0];
  if (!firstErrorKey) return;

  const element = document.getElementById(firstErrorKey) ||
    document.querySelector(`[name="${firstErrorKey}"]`) ||
    document.querySelector(`[data-field="${firstErrorKey}"]`);

  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (element instanceof HTMLElement && 'focus' in element) {
      setTimeout(() => {
        (element as HTMLElement).focus();
      }, 300);
    }
  }
}

/**
 * Runs a step schema over its input and reports the first error for each field.
 *
 * Each step's input is a projection of the form: a field whose rule doesn't apply in the
 * current state is passed as `undefined`, and its schema is `.optional()`, so it is skipped.
 */
function toResult<T extends z.ZodObject>(
  schema: T,
  input: { [K in keyof z.input<T>]-?: unknown },
): ValidationResult {
  const result = schema.safeParse(input);
  const errors = result.success ? {} : firstErrorPerField(result.error);
  return {
    isValid: result.success,
    errors,
    firstErrorField: Object.keys(errors)[0],
  };
}

/** Pass a value to its schema only when the rule applies; otherwise skip it. */
const when = <T,>(applies: boolean, value: T): T | undefined => (applies ? value : undefined);

const typeOfRequestSchema = z.object({
  isPreservationRequest: answered('Please indicate if this is a preservation request'),
});

export function validateTypeOfRequest(data: TypeOfRequestForm, _isUSA: boolean): ValidationResult {
  return toResult(typeOfRequestSchema, { isPreservationRequest: data.isPreservationRequest });
}

const hasAnyCrime = (data: NatureOfCrimeForm): boolean =>
  Object.entries(data)
    .filter(([key]) => key !== 'isOtherDescription')
    .some(([, value]) => value === true);

const natureOfCrimeSchema = z.object({
  cpConcern: answered('Please indicate if this request is related to child exploitation'),
  natureOfCrime: z
    .custom<NatureOfCrimeForm>()
    .refine(hasAnyCrime, { error: 'Please select the nature of crime' })
    .optional(),
  isOtherDescription: requiredText('Please describe the other type of crime').optional(),
});

/**
 * cpConcern is collected on this step now (moved from Type of Request).
 * - null: user has not answered yet — block.
 * - true: CE confirmed — the crime checkbox list is not required.
 * - false: not CE — at least one crime checkbox must be selected.
 */
export function validateNatureOfCrime(
  data: NatureOfCrimeForm,
  cpConcern: boolean | null,
): ValidationResult {
  return toResult(natureOfCrimeSchema, {
    cpConcern,
    natureOfCrime: when(cpConcern === false, data),
    isOtherDescription: when(Boolean(data.isOther), data.isOtherDescription ?? ''),
  });
}

const INVALID_EMAIL = 'Please enter a valid email address';

const agentInformationFields = {
  agencyName: requiredText('Agency name is required'),
  agentFirstName: requiredText('First name is required'),
  agentLastName: requiredText('Last name is required'),
  agentEmail: requiredEmail('Email address is required', INVALID_EMAIL),
};

const receivingAgentEmailSchema = requiredEmail(
  'Recipient\'s government email address is required',
  INVALID_EMAIL,
).optional();

const usAgentInformationSchema = z.object({
  ...agentInformationFields,
  agentPhoneNumber: usPhoneSchema,
  receivingAgentEmail: receivingAgentEmailSchema,
});

const internationalAgentInformationSchema = z.object({
  ...agentInformationFields,
  agentPhoneNumber: internationalPhoneSchema,
  receivingAgentEmail: receivingAgentEmailSchema,
});

export function validateAgentInformation(data: AgentInformationForm, isInternational: boolean = false): ValidationResult {
  const schema = isInternational ? internationalAgentInformationSchema : usAgentInformationSchema;
  return toResult(schema, {
    agencyName: data.agencyName,
    agentFirstName: data.agentFirstName,
    agentLastName: data.agentLastName,
    agentEmail: data.agentEmail,
    agentPhoneNumber: data.agentPhoneNumber,
    receivingAgentEmail: when(Boolean(data.hasReceivingAgent), data.receivingAgentEmail ?? ''),
  });
}

const DATE_OVERRIDE_REQUEST_TYPES = ['PR/TT', 'Lawful Intercept'];

const identifiersSchema = z
  .array(z.custom<IdentifierFormValue>())
  .min(1, 'Please add at least one identifier')
  .superRefine((identifiers, ctx) => {
    if (identifiers.some((id) => !id.type?.trim() || !id.value?.trim())) {
      ctx.addIssue({ code: 'custom', message: 'All identifiers must have both type and value' });
      return;
    }
    const unsupportedTypes = Array.from(
      new Set(identifiers.map((id) => id.type).filter((type) => !isIdentifierTypeSupported(type))),
    );
    if (unsupportedTypes.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: `These identifier types can't be submitted yet: ${unsupportedTypes.join(', ')}. Remove them to continue.`,
      });
    }
  });

const dataCategoriesSchema = z
  .array(z.string())
  .min(1, 'Please select at least one data category')
  .superRefine((categories, ctx) => {
    const unsupportedCategories = categories.filter((category) => !isDataCategorySupported(category));
    if (unsupportedCategories.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: `These data categories can't be submitted yet: ${unsupportedCategories.join(', ')}. Remove them to continue.`,
      });
    }
  });

/** The first rule a request date range breaks, or `null` if it's valid. */
function requestDateRangeError(
  start: string | null,
  end: string | null,
  requestType: string | null | undefined,
): string | null {
  if (!start || !end) {
    return 'Both start date and end date are required';
  }

  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  if (![sy, sm, sd, ey, em, ed].every(Number.isFinite)) {
    return 'Please enter valid dates';
  }

  const startDate = new Date(sy, sm - 1, sd);
  const endDate = new Date(ey, em - 1, ed);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const minDate = new Date(1990, 0, 1);

  if (startDate < minDate || endDate < minDate) {
    return 'Dates cannot be before January 1, 1990';
  }
  if (startDate > endDate) {
    return 'Start date must be before end date';
  }
  if (endDate > today && !DATE_OVERRIDE_REQUEST_TYPES.includes(requestType ?? '')) {
    return 'End date cannot be in the future';
  }
  return null;
}

const requestDateRangeSchema = z
  .object({
    start: z.string().nullable(),
    end: z.string().nullable(),
    requestType: z.string().nullish(),
  })
  .superRefine(({ start, end, requestType }, ctx) => {
    const message = requestDateRangeError(start, end, requestType);
    if (message) {
      ctx.addIssue({ code: 'custom', message });
    }
  });

const typeOfServiceSchema = z.object({
  services: z.array(z.string()).min(1, 'Please select at least one Microsoft service'),
  identifiers: identifiersSchema,
  dataCategories: dataCategoriesSchema,
  requestDateRange: requestDateRangeSchema,
});

export function validateTypeOfService(data: TypeOfServiceForm, requestType?: string | null): ValidationResult {
  return toResult(typeOfServiceSchema, {
    services: [...data.manualServices, ...data.selectedServiceIds],
    identifiers: data.identifiers,
    dataCategories: data.dataCategories,
    requestDateRange: { start: data.requestStartDate, end: data.requestEndDate, requestType },
  });
}

const legalOrderDetailsSchema = (requestType: string | null | undefined) =>
  z.object({
    leReferenceNumber: requiredText('LE Reference Number is required'),
    requestType: z.string().min(1, 'Request type is required').optional(),
    requestSubType: z.string().min(1, `Request sub-type is required for ${requestType}`).optional(),
  });

export function validateLegalOrderDetails(
  typeOfRequest: TypeOfRequestForm,
  _isUSA: boolean
): ValidationResult {
  const { requestType, requestSubType } = typeOfRequest;
  const isNonPreservation = typeOfRequest.isPreservationRequest === false;
  const requiresSubtype = requestType === 'PR/TT' || requestType === 'Lawful Intercept';

  return toResult(legalOrderDetailsSchema(requestType), {
    leReferenceNumber: typeOfRequest.leReferenceNumber,
    requestType: when(isNonPreservation, requestType ?? ''),
    requestSubType: when(isNonPreservation && requiresSubtype, requestSubType ?? ''),
  });
}

const fileUploadSchema = (hasNdo: boolean | null) =>
  z.object({
    files: z.array(z.unknown()).min(1, 'Please upload at least one file (legal order document)'),
    hasNdo: answered('Please indicate if you are submitting a Non-Disclosure Order').optional(),
    isNdoConfirm: z
      .literal(true, {
        error: hasNdo
          ? 'Please confirm that you have uploaded a compliant Non-Disclosure Order'
          : 'Please acknowledge that Microsoft will take steps to notify the user or customer',
      })
      .optional(),
  });

export function validateFileUpload(
  data: FileUploadForm,
  ndo: NDOForm,
  isUSA: boolean,
  isChildExploitation: boolean,
  isPreservationRequest: boolean = false
): ValidationResult {
  const ndoApplies = isUSA && !isPreservationRequest && !isChildExploitation;

  return toResult(fileUploadSchema(ndo.hasNdo), {
    files: data.files,
    hasNdo: when(ndoApplies, ndo.hasNdo),
    isNdoConfirm: when(ndoApplies && ndo.hasNdo !== null, Boolean(ndo.isNdoConfirm)),
  });
}

const domesticReviewSchema = z.object({
  acknowledgement: acknowledged('Please type "I acknowledge" to confirm').optional(),
  isWAShieldLaw: answered('Please answer the Washington Shield Law question'),
  waShieldLawAcknowledgement: acknowledged('Please type "I acknowledge" for Washington Shield Law').optional(),
  perjuryAcknowledgement: acknowledged('Please type "I acknowledge" for the perjury declaration'),
});

export function validateDomesticReview(
  data: DomesticReviewForm,
  typeOfRequest: TypeOfRequestForm
): ValidationResult {
  const isPreservation = typeOfRequest.isPreservationRequest === true;
  const isChildExploitation = typeOfRequest.cpConcern === true;

  return toResult(domesticReviewSchema, {
    acknowledgement: when(!isPreservation && !isChildExploitation, data.acknowledgement),
    isWAShieldLaw: typeOfRequest.isWAShieldLaw,
    waShieldLawAcknowledgement: when(typeOfRequest.isWAShieldLaw === true, typeOfRequest.waShieldLawAcknowledgement),
    perjuryAcknowledgement: data.perjuryAcknowledgement,
  });
}


export function validateDomesticStep(
  step: number,
  wizardState: {
    typeOfRequest: TypeOfRequestForm;
    natureOfCrime: NatureOfCrimeForm;
    ndo: NDOForm;
    agentInfo: AgentInformationForm;
    typeOfService: TypeOfServiceForm;
    fileUpload: FileUploadForm;
    review: DomesticReviewForm;
  }
): ValidationResult {
  switch (step) {
    case 0:
      return validateTypeOfRequest(wizardState.typeOfRequest, true);
    case 1:
      return validateNatureOfCrime(wizardState.natureOfCrime, wizardState.typeOfRequest.cpConcern);
    case 2:
      return validateAgentInformation(wizardState.agentInfo as AgentInformationForm, false);
    case 3:
      return validateTypeOfService(wizardState.typeOfService, wizardState.typeOfRequest.requestType);
    case 4:
      return validateLegalOrderDetails(wizardState.typeOfRequest, true);
    case 5:
      return validateFileUpload(
        wizardState.fileUpload,
        wizardState.ndo,
        true,
        wizardState.typeOfRequest.cpConcern === true,
        wizardState.typeOfRequest.isPreservationRequest === true
      );
    case 6:
      return validateDomesticReview(wizardState.review, wizardState.typeOfRequest);
    default:
      return { isValid: true, errors: {} };
  }
}
